import React, { useRef } from 'react';
import { useInView, useMotionValue, useTransform, useReducedMotion } from 'framer-motion';
import { motion } from '../../lib/motionShim';
import { useViewportProgress } from '../../lib/useViewportProgress';

/**
 * The quiet section — and the page's thesis as an interaction.
 *
 * Your numbers drift in the dark at different depths. An ECG pulse keeps
 * firing behind them: the body, still transmitting. Bring the cursor close
 * to any fragment and it brightens — look away and it fades again. You can
 * read any single signal. Nothing connects them. That's the gap.
 */

const fragments = [
  { text: '2,140 kcal', dot: '#fb923c', x: '7%', y: '15%', speed: -1.4, far: false },
  { text: '8,432 steps', dot: '#818cf8', x: '76%', y: '9%', speed: 1.1, far: true },
  { text: '47-day streak', dot: '#f87171', x: '14%', y: '80%', speed: 1.6, far: false },
  { text: '7.2h sleep', dot: '#818cf8', x: '83%', y: '70%', speed: -1.2, far: true },
  { text: '68g protein', dot: '#4ade80', x: '63%', y: '88%', speed: 0.9, far: false },
  { text: 'HbA1c 5.4%', dot: '#f87171', x: '28%', y: '5%', speed: -0.8, far: true },
  { text: '71.2 kg', dot: '#a855f7', x: '5%', y: '46%', speed: 1.3, far: false },
  { text: 'Vit D 31.39', dot: '#fb923c', x: '88%', y: '40%', speed: -1.7, far: false },
  { text: '85% recovery', dot: '#4ade80', x: '35%', y: '92%', speed: -1.0, far: true },
  { text: '220 kg leg press', dot: '#a855f7', x: '68%', y: '22%', speed: 1.4, far: true },
];

/* One ECG beat: flat — P bump — QRS spike — T bump — flat */
const beat = (o) =>
  `L${o + 52} 150 L${o + 64} 141 L${o + 76} 150 L${o + 88} 150 L${o + 96} 160 ` +
  `L${o + 104} 82 L${o + 112} 186 L${o + 120} 150 L${o + 150} 150 L${o + 168} 135 L${o + 186} 150`;
const ECG_PATH =
  'M-40 150 ' + [0, 400, 800, 1200, 1600].map(beat).join(' ') + ' L2060 150';

const clamp01 = (v) => Math.min(1, Math.max(0, v));
const smooth = (v) => v * v * (3 - 2 * v);

const Fragment = ({ f, index, mouseX, mouseY, progress, isInView, reduce, size }) => {
  const ref = useRef(null);
  const fx = parseFloat(f.x);
  const fy = parseFloat(f.y);

  // Convergence: past the section's midpoint the signals stop drifting apart
  // and start gathering toward the center — then get absorbed.
  const convergeX = useTransform(progress, (p) =>
    reduce ? 0 : smooth(clamp01((p - 0.58) / 0.34)) * ((50 - fx) / 100) * size.w * 0.85
  );
  const parallaxAndConvergeY = useTransform(progress, (p) => {
    if (reduce) return 0;
    const parallax = f.speed * 42 * (1 - 2 * p);
    const converge =
      smooth(clamp01((p - 0.58) / 0.34)) * ((52 - fy) / 100) * size.h * 0.72;
    return parallax + converge;
  });
  const absorb = useTransform(progress, (p) =>
    reduce ? 1 : 1 - smooth(clamp01((p - 0.8) / 0.16))
  );

  // Proximity glow: brightens near the cursor, decays with distance
  const glow = useTransform([mouseX, mouseY], ([x, y]) => {
    if (reduce || x === null || !ref.current) return 0;
    const r = ref.current.getBoundingClientRect();
    const d = Math.hypot(x - (r.left + r.width / 2), y - (r.top + r.height / 2));
    return Math.exp(-(d * d) / (2 * 150 * 150));
  });
  const opacity = useTransform([glow, absorb], ([g, a]) => (0.3 + g * 0.7) * a);
  const dotOpacity = useTransform([glow, absorb], ([g, a]) => (0.45 + g * 0.55) * a);

  return (
    <motion.span
      className="absolute"
      style={{ left: f.x, top: f.y, x: convergeX, y: parallaxAndConvergeY }}
      initial={{ opacity: 0 }}
      animate={isInView ? { opacity: 1 } : {}}
      transition={{ duration: 1.4, delay: 0.25 + index * 0.12 }}
    >
      {/* Slow independent drift — every signal on its own rhythm */}
      <motion.span
        className="block"
        animate={reduce ? {} : { y: [0, -7 - (index % 3) * 3, 0] }}
        transition={
          reduce
            ? {}
            : {
                duration: 5.5 + (index % 4) * 1.3,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: index * 0.4,
              }
        }
      >
        <motion.span
          ref={ref}
          className={`inline-flex items-center gap-2 font-mono tracking-wider whitespace-nowrap text-ston-text ${
            f.far ? 'text-[11px] blur-[0.6px]' : 'text-[12.5px] md:text-[13.5px]'
          }`}
          style={{ opacity }}
        >
          <motion.span
            className="h-1.5 w-1.5 rounded-full"
            style={{ background: f.dot, opacity: dotOpacity, boxShadow: `0 0 8px ${f.dot}55` }}
          />
          {f.text}
        </motion.span>
      </motion.span>
    </motion.span>
  );
};

const TheGap = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-160px' });
  // Live visibility — the ECG repaints every frame, so it only runs on screen
  const isOnScreen = useInView(ref, { margin: '150px' });
  const reduce = useReducedMotion();

  const scrollYProgress = useViewportProgress(ref);

  // Section dimensions, so convergence targets the true center
  const [size, setSize] = React.useState({ w: 1440, h: 900 });
  React.useEffect(() => {
    const measure = () => {
      const r = ref.current?.getBoundingClientRect();
      if (r && r.width > 0) setSize({ w: r.width, h: r.height });
    };
    measure();
    window.addEventListener('resize', measure, { passive: true });
    return () => window.removeEventListener('resize', measure);
  }, []);

  const mouseX = useMotionValue(null);
  const mouseY = useMotionValue(null);
  const handleMove = (e) => {
    mouseX.set(e.clientX);
    mouseY.set(e.clientY);
  };
  const handleLeave = () => {
    mouseX.set(null);
    mouseY.set(null);
  };

  // The ECG drifts slightly against scroll — alive, independent of you
  const ecgY = useTransform(scrollYProgress, [0, 1], [30, -30]);

  return (
    <section
      id="the-gap"
      ref={ref}
      className="relative py-40 md:py-56 bg-ston-bg overflow-hidden"
      data-testid="gap-section"
      onMouseMove={reduce ? undefined : handleMove}
      onMouseLeave={reduce ? undefined : handleLeave}
    >
      {/* ECG — the body, still transmitting */}
      <motion.div
        aria-hidden="true"
        className="absolute left-0 right-0 top-1/2 -translate-y-1/2 pointer-events-none"
        style={{ y: reduce ? 0 : ecgY }}
      >
        <svg
          viewBox="0 0 2000 300"
          preserveAspectRatio="none"
          className="w-full h-[300px]"
        >
          {/* Faint trace */}
          <path
            d={ECG_PATH}
            fill="none"
            stroke="rgba(168,85,247,0.09)"
            strokeWidth="1.5"
          />
          {/* Traveling pulse — repaints per frame, so only while visible */}
          {!reduce && isOnScreen && (
            <motion.path
              d={ECG_PATH}
              fill="none"
              stroke="rgba(168,85,247,0.55)"
              strokeWidth="2"
              strokeLinecap="round"
              pathLength="100"
              strokeDasharray="6 94"
              style={{ filter: 'drop-shadow(0 0 7px rgba(168,85,247,0.9))' }}
              animate={{ strokeDashoffset: [100, 0] }}
              transition={{ duration: 10, repeat: Infinity, ease: 'linear' }}
            />
          )}
        </svg>
      </motion.div>

      {/* Drifting fragments */}
      <div aria-hidden="true" className="absolute inset-0 hidden sm:block pointer-events-none select-none">
        {fragments.map((f, i) => (
          <Fragment
            key={f.text}
            f={f}
            index={i}
            mouseX={mouseX}
            mouseY={mouseY}
            progress={scrollYProgress}
            isInView={isInView}
            reduce={reduce}
            size={size}
          />
        ))}
      </div>

      <div className="relative max-w-[880px] mx-auto px-6 text-center pointer-events-none">
        <h2
          className="font-outfit font-extrabold italic uppercase leading-[1.04]"
          style={{ fontSize: 'clamp(28px, 4.2vw, 56px)', letterSpacing: '-0.025em' }}
        >
          {/* Controlled breaks — no orphaned words, each line rises on its own */}
          {[
            { text: 'Your body has been', cls: 'text-ston-text', delay: 0.35 },
            { text: 'sending signals for years.', cls: 'text-ston-text', delay: 0.5 },
          ].map((line) => (
            <span key={line.text} className="block overflow-hidden">
              <motion.span
                className={`block ${line.cls}`}
                initial={{ opacity: 0, y: reduce ? 0 : '70%' }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.9, delay: line.delay, ease: [0.16, 1, 0.3, 1] }}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
          <span className="mt-3 block overflow-hidden">
            <motion.span
              className="block text-ston-text-muted"
              initial={{ opacity: 0, y: reduce ? 0 : '70%' }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.9, delay: 0.72, ease: [0.16, 1, 0.3, 1] }}
            >
              Nothing has been{' '}
              <span className="relative inline-block">
                reading them.
                {/* The scanner: a purple line sweeps across, "reading" the words */}
                <motion.span
                  aria-hidden="true"
                  className="absolute left-0 -bottom-1 md:-bottom-2 h-[3px] rounded-full bg-gradient-to-r from-ston-purple to-ston-purple-light"
                  style={{ width: '100%', transformOrigin: 'left', boxShadow: '0 0 14px rgba(168,85,247,0.8)' }}
                  initial={{ scaleX: 0 }}
                  animate={isInView ? { scaleX: 1 } : {}}
                  transition={{ duration: 1.1, delay: 1.9, ease: [0.16, 1, 0.3, 1] }}
                />
              </span>
            </motion.span>
          </span>
        </h2>

        <motion.p
          className="mt-8 mx-auto max-w-[54ch] text-[16.5px] md:text-[18px] leading-relaxed text-ston-text-secondary"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.9, delay: 1.0 }}
        >
          A step count. A calorie total. A lab report filed away unread. Each one a
          fragment — none of them talking to each other, and nobody telling you which
          one actually matters this month.
        </motion.p>

        <motion.div
          aria-hidden="true"
          className="mx-auto mt-14 h-12 w-px bg-gradient-to-b from-ston-purple/40 to-transparent"
          initial={{ opacity: 0, scaleY: 0 }}
          animate={isInView ? { opacity: 1, scaleY: 1 } : {}}
          transition={{ duration: 0.8, delay: 1.4 }}
          style={{ transformOrigin: 'top' }}
        />
      </div>
    </section>
  );
};

export default TheGap;
